import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  VStack,
  Text,
  Badge,
  Divider,
  Flex,
  Avatar,
  useToast,
  Spinner,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { markMessagesAsRead, deleteMessage } from '../../Redux/Login/action';
import { DeleteIcon, RepeatIcon, ArrowForwardIcon, SearchIcon, WarningIcon, CheckIcon } from '@chakra-ui/icons';
import SendMessageModal from './SendMessageModal';

// Message bubble component
const MessageBubble = ({ message, handleMarkAsRead, handleDelete, handleReply }) => {
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = React.useState(false);
  const cancelRef = React.useRef();
  
  return (
    <Box width="100%" mb={4}>
      <Flex justify="space-between" mb={1}>
        <Flex align="center">
          <Avatar size="xs" name={message.fromBurrowerId} mr={2} />
          <Text fontSize="sm" fontWeight="bold">{message.fromBurrowerId}</Text>
        </Flex>
        <Text fontSize="xs" color="gray.500">
          {message.formattedTime}
        </Text>
      </Flex>
      
      <Box 
        bg="blue.900" 
        p={3} 
        borderRadius="lg" 
        borderTopLeftRadius="sm"
        maxW="90%"
        position="relative"
        boxShadow="sm"
      >
        <Text color="white">{message.userMessageContents}</Text>
        
        <Flex mt={2} justify="flex-end" opacity={0.7}>
          {!message.isRead && (
            <Tooltip label="Mark as read">
              <IconButton
                icon={<CheckIcon />}
                size="xs"
                variant="ghost"
                onClick={() => handleMarkAsRead(message.userMessageId)}
                aria-label="Mark as read"
              />
            </Tooltip>
          )}
          
          <Tooltip label="Reply">
            <IconButton
              icon={<RepeatIcon />}
              size="xs"
              variant="ghost"
              onClick={() => handleReply(message.fromBurrowerId)}
              aria-label="Reply"
              ml={1}
            />
          </Tooltip>
          
          <Tooltip label="Delete">
            <IconButton
              icon={<DeleteIcon />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => setIsDeleteAlertOpen(true)}
              aria-label="Delete message"
              ml={1}
            />
          </Tooltip>
        </Flex>
      </Box>
      
      {/* Delete confirmation alert */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteAlertOpen(false)}
        size="sm"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Message
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteAlertOpen(false)}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={() => {
                  handleDelete(message.userMessageId);
                  setIsDeleteAlertOpen(false);
                }}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

const UserMessagesModal = ({ isOpen, onClose, messages = [], onSendMessage }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { markingRead, deletingMessage } = useSelector(state => state.auth.messages || {});
  const [replyTo, setReplyTo] = useState(null);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [localMessages, setLocalMessages] = useState(messages);
  const [searchQuery, setSearchQuery] = useState('');

  // Keep localMessages in sync with props
  React.useEffect(() => {
    setLocalMessages(messages);
  }, [messages]);
  
  // Format time ago function (replacement for date-fns formatDistanceToNow)
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    // Time intervals in seconds
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1
    };
    
    let counter;
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      counter = Math.floor(seconds / secondsInUnit);
      if (counter > 0) {
        return counter === 1 ? `1 ${unit} ago` : `${counter} ${unit}s ago`;
      }
    }
    
    return 'just now';
  };
  
  // Process messages with formatted time
  const processedMessages = localMessages.map(msg => ({
    ...msg,
    formattedTime: formatTimeAgo(msg.sentAt)
  }));
  
  // Sort messages by date, newest first
  const sortedMessages = [...processedMessages].sort((a, b) => 
    new Date(b.sentAt) - new Date(a.sentAt)
  );
  
  // Filter messages based on search query
  const filteredMessages = searchQuery 
    ? sortedMessages.filter(msg => 
        msg.userMessageContents.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.fromBurrowerId.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sortedMessages;
  
  // Find unread message IDs
  const unreadMessageIds = localMessages
    .filter(msg => !msg.isRead)
    .map(msg => msg.userMessageId);
  
  const handleMarkAllAsRead = async () => {
    if (unreadMessageIds.length === 0) {
      toast({
        title: "No unread messages",
        status: "info",
        duration: 3000,
      });
      return;
    }
    
    const result = await dispatch(markMessagesAsRead(unreadMessageIds, 'user'));
    
    if (result.success) {
      toast({
        title: "Messages marked as read",
        description: `${result.updateCount} messages updated`,
        status: "success",
        duration: 3000,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to mark messages as read",
        status: "error",
        duration: 3000,
      });
    }
  };
  
  const handleMarkAsRead = async (messageId) => {
    const result = await dispatch(markMessagesAsRead([messageId], 'user'));
    
    if (result.success) {
      toast({
        title: "Message marked as read",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to mark message as read",
        status: "error",
        duration: 3000,
      });
    }
  };
  
  const handleReply = (fromBurrowerId) => {
    setReplyTo(fromBurrowerId);
    setIsReplyModalOpen(true);
  };
  
  const handleCloseReplyModal = () => {
    setIsReplyModalOpen(false);
    setReplyTo(null);
  };
  
  const handleDeleteMessage = async (messageId) => {
    setDeletingId(messageId);
    try {
      const result = await dispatch(deleteMessage(messageId, 'user'));
      
      if (result.success) {
        toast({
          title: "Message deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Update local state to remove the message
        setLocalMessages(prev => prev.filter(msg => msg.userMessageId !== messageId));
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete message",
          status: "error",
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setDeletingId(null);
    }
  };
  
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="80vh" bg="gray.800" color="white">
          <ModalHeader display="flex" justifyContent="space-between" alignItems="center">
            <Text>User Messages</Text>
            <Badge colorScheme="blue" fontSize="0.8em" ml={2}>
              {unreadMessageIds.length} Unread
            </Badge>
          </ModalHeader>
          <ModalCloseButton />
          
          <Box px={6} pb={2}>
            <InputGroup size="md">
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                pr="4.5rem"
              />
              <InputRightElement width="4.5rem">
                <Button h="1.75rem" size="sm" onClick={() => setSearchQuery('')}>
                  {searchQuery ? 'Clear' : <SearchIcon />}
                </Button>
              </InputRightElement>
            </InputGroup>
          </Box>
          
          <ModalBody>
            {markingRead && (
              <Flex justify="center" my={4}>
                <Spinner />
              </Flex>
            )}
            
            {localMessages.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text fontSize="lg">No messages yet</Text>
                <Text color="gray.500">When you receive messages, they will appear here</Text>
              </Box>
            ) : filteredMessages.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text fontSize="lg">No matching messages</Text>
                <Text color="gray.500">Try a different search term</Text>
                <Button mt={4} size="sm" onClick={() => setSearchQuery('')}>Clear Search</Button>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch" divider={<Divider />}>
                {filteredMessages.map(message => (
                  <MessageBubble
                    key={message.userMessageId}
                    message={message}
                    handleMarkAsRead={handleMarkAsRead}
                    handleDelete={handleDeleteMessage}
                    handleReply={handleReply}
                  />
                ))}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button 
              colorScheme="blue" 
              mr={3} 
              onClick={handleMarkAllAsRead}
              isDisabled={unreadMessageIds.length === 0}
              size="sm"
            >
              Mark All as Read
            </Button>
            <Button variant="ghost" onClick={onClose} size="sm">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      <SendMessageModal 
        isOpen={isReplyModalOpen} 
        onClose={handleCloseReplyModal} 
        initialRecipient={replyTo} 
      />
    </>
  );
};

export default UserMessagesModal;
